import { useMutation } from '@tanstack/react-query';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { AccountOperationsViewModel } from '../../../viewmodels/AdminViewModels';
import { InternalMeetingNoteType, ICustomerMeeting, IMeetingReference } from '../../../models/AdminModels';
import {
	CsmMeetingTypes,
	IAccount,
	IUser,
	RichContentContextSource,
	RichContentReferenceMethod,
} from '../../../extViewmodels';
import { wrapInDocumentTags } from './textToHtmlUtils';

export interface IAddMeetingNoteRequest {
	account: IAccount;
	meetingType: CsmMeetingTypes;
	meetingStatus: string;
	durationInMinutes: number;
	meetingTags?: string[];
	noteHtml: string;
	/** Optional user to CC on the meeting note (e.g., Success Specialist for Implementation meetings) */
	ccUser?: IUser;
	/** Actual date/time the meeting took place. Seeded from the source transcript, or entered for manual notes. */
	meetingDate?: string;
	/** Hard reference to the specific meeting (Zoom recording / calendar event). Omitted for manual, date-only notes. */
	meetingReference?: IMeetingReference;
}

export function useAdminAccountMeetingNoteMutation() {
	const userSession = useAdminUserSession();

	return useMutation({
		mutationFn: async (request: IAddMeetingNoteRequest) => {
			const accountOperations = new AccountOperationsViewModel(userSession, request.account);

			// Wrap HTML in lev-content document tags
			const documentContent = wrapInDocumentTags(request.noteHtml);

			// Create the meeting request payload with IRawRichTextContentState format
			const meetingRequest: ICustomerMeeting = {
				durationInMinutes: request.durationInMinutes,
				meetingStatus: request.meetingStatus,
				meetingTags: request.meetingTags || [],
				meetingType: request.meetingType,
				metWith: InternalMeetingNoteType.CSM,
				meetingDate: request.meetingDate,
				meetingReference: request.meetingReference,
				note: {
					content: {
						document: documentContent,
						documentVersion: 1,
					},
					context: {
						source: RichContentContextSource.Meeting,
						meetingType: request.meetingType,
						meetingStatus: request.meetingStatus,
						durationInMinutes: request.durationInMinutes,
						meetingTags: request.meetingTags || [],
					},
					// Add CC user to referencedEntities if provided
					...(request.ccUser && {
						referencedEntities: {
							users: [
								{
									method: RichContentReferenceMethod.Explicit,
									entity: {
										id: request.ccUser.id,
										firstName: request.ccUser.firstName,
										lastName: request.ccUser.lastName,
									},
								},
							],
						},
					}),
				},
			};

			return accountOperations.addAccountMeetingNote(request.account, meetingRequest);
		},
	});
}
